import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, Globe2, MapPin, X } from 'lucide-react';
import { clsx } from 'clsx';
import {
  clearActivityScore,
  readWorkbookJson,
  writeActivityScore,
  writeWorkbookJson,
  type WorkbookActivityCompletion,
} from './workbookPersist';

export type MapLabelQuizHotspot = { x: number; y: number };

export type MapLabelQuizItem = {
  id: number;
  answer: string;
  options: string[];
  hotspot: MapLabelQuizHotspot;
};

export interface MapLabelQuizCardProps {
  workbookLabel?: string;
  title: string;
  instructions?: string;
  sourcePage?: number;
  mapImage: string;
  items: MapLabelQuizItem[];
  persistenceId?: string;
  onInteracted?: () => void;
  onWorkbookStatusChange?: (status: WorkbookActivityCompletion) => void;
}

type ItemStatus = 'idle' | 'wrong' | 'correct';

type PersistShape = {
  version: 2;
  /** Persisted only for `correct` — wrong is session-only */
  status: Record<string, 'correct'>;
};

function norm(s: string): string {
  let t = String(s).trim().replace(/\s+/g, ' ');
  try {
    t = t.normalize('NFKC');
  } catch {
    /* ignore */
  }
  return t;
}

function isMatch(a: string, b: string): boolean {
  return norm(a) === norm(b);
}

export const MapLabelQuizCard: React.FC<MapLabelQuizCardProps> = ({
  workbookLabel,
  title,
  instructions,
  sourcePage,
  mapImage,
  items,
  persistenceId,
  onInteracted,
  onWorkbookStatusChange,
}) => {
  const storageKey = persistenceId ? `g3-mapquiz-${persistenceId}` : '';

  const itemsRef = useRef(items);
  itemsRef.current = items;

  const itemsStableKey = items
    .map(
      (it) =>
        `${it.id}\u0001${it.hotspot.x}\u0001${it.hotspot.y}\u0001${norm(it.answer)}\u0001${it.options.map(norm).join('\u0002')}`
    )
    .sort()
    .join('\u0003');

  const sortedItems = useMemo(() => [...itemsRef.current].sort((a, b) => a.id - b.id), [itemsStableKey]);

  const emptyMaps = useMemo(() => {
    const s: Record<number, ItemStatus> = {};
    const a: Record<number, string> = {};
    for (const it of itemsRef.current) {
      s[it.id] = 'idle';
      a[it.id] = '';
    }
    return { status: s, answers: a };
  }, [itemsStableKey]);

  const [statusById, setStatusById] = useState<Record<number, ItemStatus>>(() => emptyMaps.status);
  const [answersById, setAnswersById] = useState<Record<number, string>>(() => emptyMaps.answers);
  const [openHotspotId, setOpenHotspotId] = useState<number | null>(null);
  const [sessionTouched, setSessionTouched] = useState(false);
  const [mapBroken, setMapBroken] = useState(false);

  useEffect(() => {
    const list = itemsRef.current;
    const nextStatus: Record<number, ItemStatus> = {};
    const nextAnswers: Record<number, string> = {};
    for (const it of list) {
      nextStatus[it.id] = 'idle';
      nextAnswers[it.id] = '';
    }

    if (!storageKey) {
      setStatusById(nextStatus);
      setAnswersById(nextAnswers);
    setOpenHotspotId(null);
    setSessionTouched(false);
    setMapBroken(false);
    return;
    }

    const loaded = readWorkbookJson<Partial<PersistShape> | null>(storageKey, null);
    if (loaded?.version === 2 && loaded.status && typeof loaded.status === 'object') {
      const raw = loaded.status as Record<string, unknown>;
      for (const it of list) {
        if (raw[String(it.id)] === 'correct') {
          nextStatus[it.id] = 'correct';
          nextAnswers[it.id] = it.answer;
        }
      }
    }

    setStatusById(nextStatus);
    setAnswersById(nextAnswers);
    setOpenHotspotId(null);
    setSessionTouched(Object.values(nextStatus).some((x) => x === 'correct'));
  }, [storageKey, itemsStableKey, items.length]);

  useEffect(() => {
    if (!storageKey) return;
    const status: Record<string, 'correct'> = {};
    for (const it of items) {
      if (statusById[it.id] === 'correct') status[String(it.id)] = 'correct';
    }
    writeWorkbookJson(storageKey, { version: 2, status } satisfies PersistShape);
  }, [storageKey, statusById, items]);

  const mark = useCallback(() => onInteracted?.(), [onInteracted]);

  const correctCount = useMemo(() => items.filter((it) => statusById[it.id] === 'correct').length, [items, statusById]);
  const total = items.length;
  const allCorrect = total > 0 && correctCount === total;

  useEffect(() => {
    if (!onWorkbookStatusChange) return;
    if (total === 0) {
      onWorkbookStatusChange('completed');
      return;
    }
    if (allCorrect) {
      onWorkbookStatusChange('completed');
      return;
    }
    if (sessionTouched || correctCount > 0) {
      onWorkbookStatusChange('in_progress');
      return;
    }
    onWorkbookStatusChange('not_started');
  }, [onWorkbookStatusChange, total, allCorrect, sessionTouched, correctCount]);

  useEffect(() => {
    if (!persistenceId || total === 0) return;
    const settled = items.every((it) => {
      const s = statusById[it.id];
      return s === 'correct' || s === 'wrong';
    });
    if (!settled) return;
    writeActivityScore(persistenceId, (correctCount / total) * 100);
  }, [persistenceId, total, items, statusById, correctCount]);

  const openFor = (id: number) => {
    if (statusById[id] === 'correct' || statusById[id] === 'wrong') return;
    mark();
    setSessionTouched(true);
    setOpenHotspotId(id);
  };

  const closePanel = () => setOpenHotspotId(null);

  const pickOption = (item: MapLabelQuizItem, option: string) => {
    if (statusById[item.id] === 'correct' || statusById[item.id] === 'wrong') return;
    mark();
    setSessionTouched(true);

    if (isMatch(option, item.answer)) {
      setStatusById((prev) => ({ ...prev, [item.id]: 'correct' }));
      setAnswersById((prev) => ({ ...prev, [item.id]: option }));
      setOpenHotspotId(null);
      return;
    }

    setStatusById((prev) => ({ ...prev, [item.id]: 'wrong' }));
    setAnswersById((prev) => ({ ...prev, [item.id]: option }));
    setOpenHotspotId(null);
  };

  const resetAll = () => {
    mark();
    const z: Record<number, ItemStatus> = {};
    const za: Record<number, string> = {};
    for (const it of items) {
      z[it.id] = 'idle';
      za[it.id] = '';
    }
    setStatusById(z);
    setAnswersById(za);
    setOpenHotspotId(null);
    setSessionTouched(false);
    setMapBroken(false);
    if (storageKey) writeWorkbookJson(storageKey, { version: 2, status: {} } satisfies PersistShape);
    if (persistenceId) clearActivityScore(persistenceId);
    onWorkbookStatusChange?.('not_started');
  };

  const activeItem = openHotspotId != null ? items.find((i) => i.id === openHotspotId) : undefined;
  const activeOrdinal = activeItem ? sortedItems.findIndex((i) => i.id === activeItem.id) + 1 : 0;

  const renderOptionChips = (item: MapLabelQuizItem) => (
    <div
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
      role="group"
      aria-label="விடை விருப்பங்கள்"
    >
      {item.options.map((opt, optIdx) => {
        const chipBase =
          'min-h-[3.25rem] w-full rounded-2xl border-2 px-4 py-3 text-center text-base font-extrabold leading-snug transition tamil-text shadow-sm sm:min-h-[3.5rem] sm:text-lg focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-indigo-300/80';

        const idle = clsx(
          chipBase,
          'border-slate-200 bg-white text-slate-900 hover:border-indigo-300 hover:bg-indigo-50/80 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-indigo-500 dark:hover:bg-slate-700'
        );

        return (
          <button
            key={`${item.id}-opt-${optIdx}`}
            type="button"
            className={idle}
            lang="ta"
            onClick={() => pickOption(item, opt)}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );

  if (!mapImage?.trim() || items.length === 0) {
    return (
      <div className="overflow-hidden rounded-[2rem] border-4 border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 p-8 text-center dark:border-slate-700 dark:from-slate-900 dark:to-slate-800">
        <Globe2 className="mx-auto h-14 w-14 text-slate-400" aria-hidden />
        <p className="mt-4 text-lg font-bold text-slate-700 tamil-text dark:text-slate-200" lang="ta">
          {!mapImage?.trim()
            ? 'இந்தச் செயலுக்கு உலகப் படம் இன்னும் இணைக்கப்படவில்லை.'
            : 'இந்தச் செயலுக்கு கண்ட வினாக்கள் இன்னும் சேர்க்கப்படவில்லை.'}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[2rem] border-4 border-indigo-200/90 bg-gradient-to-br from-indigo-50 via-violet-50 to-amber-50 shadow-[0_24px_60px_-14px_rgba(99,102,241,0.28)] dark:border-indigo-900/50 dark:from-indigo-950/40 dark:via-slate-900 dark:to-slate-900">
      <div className="border-b border-indigo-200/70 bg-white/55 px-5 py-5 backdrop-blur-sm dark:border-indigo-800/40 dark:bg-slate-900/50">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-start gap-3">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-lg">
              <Globe2 className="h-8 w-8" aria-hidden />
            </div>
            <div className="min-w-0">
              {workbookLabel ? (
                <p className="text-sm font-extrabold text-app-primary tamil-text dark:text-indigo-200" lang="ta">
                  {workbookLabel}
                </p>
              ) : null}
              <h2 className="mt-1 text-2xl font-extrabold text-app-primary tamil-text md:text-3xl dark:text-indigo-50" lang="ta">
                {title}
              </h2>
              {instructions ? (
                <p className="mt-2 text-lg leading-relaxed text-app-primary/95 tamil-text dark:text-indigo-100/90" lang="ta">
                  {instructions}
                </p>
              ) : null}
              {sourcePage != null ? (
                <span className="mt-3 inline-block rounded-full bg-indigo-600 px-3 py-1 text-sm font-bold text-white">p.{sourcePage}</span>
              ) : null}
            </div>
          </div>
          <div
            className="shrink-0 rounded-2xl border-2 border-indigo-300/80 bg-indigo-100/90 px-4 py-2 text-center dark:border-indigo-600 dark:bg-indigo-950/60"
            role="status"
            aria-live="polite"
          >
            <p className="text-xs font-bold uppercase tracking-wide text-app-primary dark:text-indigo-300">முன்னேற்றம்</p>
            <p className="text-lg font-black text-app-primary tamil-text dark:text-indigo-50" lang="ta">
              {correctCount} / {total} சரி
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 py-6 sm:px-8">
        <div className="relative mx-auto w-full max-w-4xl">
          <div className="relative aspect-[2/1] w-full">
            {!mapBroken ? (
              <img
                src={mapImage}
                alt=""
                className="absolute inset-0 h-full w-full rounded-2xl border-4 border-white object-contain shadow-lg dark:border-slate-600"
                loading="lazy"
                onError={() => {
                  setMapBroken(true);
                  setOpenHotspotId(null);
                }}
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center rounded-2xl border-4 border-dashed border-indigo-300 bg-indigo-50/80 p-6 text-center dark:border-indigo-700 dark:bg-slate-800/80">
                <MapPin className="h-12 w-12 text-indigo-400" aria-hidden />
                <p className="mt-2 text-base font-bold text-app-primary tamil-text dark:text-indigo-100" lang="ta">
                  உலகப் படத்தை ஏற்ற முடியவில்லை. பின்னர் முயலவும்.
                </p>
              </div>
            )}

            {!mapBroken ? (
            <div className="absolute inset-0">
              {sortedItems.map((it, idx) => {
                const st = statusById[it.id] ?? 'idle';
                const isOpen = openHotspotId === it.id;
                const label = answersById[it.id]?.trim() || it.answer;

                return (
                  <Fragment key={it.id}>
                    <button
                      type="button"
                      style={{ left: `${it.hotspot.x}%`, top: `${it.hotspot.y}%` }}
                      className={clsx(
                        'absolute z-20 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-4 text-base font-black shadow-lg transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-amber-400 sm:h-14 sm:w-14 sm:text-lg',
                        st === 'correct' &&
                          'border-emerald-600 bg-emerald-500 text-white ring-2 ring-emerald-300/90 dark:border-emerald-400 dark:bg-emerald-600',
                        st === 'wrong' &&
                          'border-rose-600 bg-rose-500 text-white ring-2 ring-rose-300/90 dark:border-rose-400 dark:bg-rose-600',
                        st === 'idle' &&
                          'border-amber-400 bg-gradient-to-br from-app-primary to-indigo-600 text-white hover:scale-110 hover:shadow-xl dark:from-app-primary dark:to-indigo-700',
                        isOpen &&
                          st !== 'correct' &&
                          'z-30 scale-110 shadow-[0_0_0_6px_rgba(251,191,36,0.45),0_0_28px_10px_rgba(99,102,241,0.45)] ring-4 ring-amber-300 ring-offset-2 ring-offset-white dark:ring-offset-slate-900'
                      )}
                      aria-label={
                        st === 'correct'
                          ? `${label} — சரி, முடிந்தது`
                          : `புள்ளி ${idx + 1}: கண்டத்தின் பெயரைத் தேர்ந்தெடுக்கவும்`
                      }
                      aria-pressed={isOpen}
                      disabled={st === 'correct' || st === 'wrong'}
                      onClick={() => openFor(it.id)}
                    >
                      {st === 'correct' ? <CheckCircle2 className="h-7 w-7 sm:h-8 sm:w-8" aria-hidden /> : idx + 1}
                    </button>

                    {st === 'correct' ? (
                      <span
                        className="pointer-events-none absolute z-10 max-w-[min(42vw,11rem)] -translate-x-1/2 translate-y-2 rounded-xl border-2 border-emerald-500/80 bg-white/95 px-2 py-1 text-center text-xs font-extrabold text-emerald-900 shadow-md tamil-text dark:bg-emerald-950/90 dark:text-emerald-50 sm:max-w-[10rem] sm:text-sm"
                        style={{ left: `${it.hotspot.x}%`, top: `calc(${it.hotspot.y}% + 28px)` }}
                        lang="ta"
                      >
                        {label}
                      </span>
                    ) : null}
                  </Fragment>
                );
              })}
            </div>
            ) : null}

          </div>

          {/* Answer tray below map (all viewports) — keeps map unobstructed */}
          {!mapBroken && activeItem && statusById[activeItem.id] === 'idle' ? (
            <div
              className="mt-6 rounded-3xl border-2 border-indigo-200/90 bg-gradient-to-b from-white to-indigo-50/50 p-5 shadow-[0_12px_40px_-12px_rgba(79,70,229,0.22)] dark:border-indigo-800/60 dark:from-slate-900 dark:to-indigo-950/35 sm:p-6"
              role="dialog"
              aria-label={`புள்ளி ${activeOrdinal} — விடை தேர்வு`}
              aria-modal="false"
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
                <div className="min-w-0 flex-1 space-y-3">
                  <div className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-3 py-1 text-xs font-black uppercase tracking-wide text-white shadow-sm">
                    <MapPin className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                    <span className="tamil-text" lang="ta">
                      புள்ளி {activeOrdinal}
                    </span>
                  </div>
                  <p className="text-lg font-black leading-snug text-app-primary tamil-text dark:text-indigo-50 sm:text-xl" lang="ta">
                    இந்த இடத்தில் எந்தக் கண்டம் உள்ளது? சரியான பெயரைத் தேர்ந்தெடு.
                  </p>
                  <p
                    className="text-xs font-semibold text-slate-600 tamil-text dark:text-slate-400 sm:text-sm"
                    lang="ta"
                  >
                    ஒரு புள்ளிக்கு ஒரு தேர்வு மட்டும். தவறானால் மீண்டும் முயல் மூலம் செயலை மீட்டமைக்கவும்.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closePanel}
                  className="inline-flex shrink-0 items-center justify-center gap-2 self-end rounded-2xl border-2 border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 sm:self-start"
                  aria-label="மூடு"
                >
                  <X className="h-5 w-5" aria-hidden />
                  <span className="tamil-text" lang="ta">
                    மூடு
                  </span>
                </button>
              </div>
              <div className="mt-6 border-t border-indigo-100/90 pt-6 dark:border-indigo-900/50">
                {renderOptionChips(activeItem)}
              </div>
            </div>
          ) : null}
        </div>

        {allCorrect ? (
          <p className="mx-auto mt-6 max-w-2xl rounded-2xl border-2 border-emerald-300 bg-emerald-50/95 px-4 py-3 text-center text-base font-bold text-emerald-900 tamil-text dark:border-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-100" lang="ta">
            <CheckCircle2 className="mx-auto mb-1 h-8 w-8 text-emerald-600 dark:text-emerald-300" aria-hidden />
            அருமை! எல்லாக் கண்டங்களும் சரியாகக் குறிக்கப்பட்டன.
          </p>
        ) : null}

        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={resetAll}
            className="min-h-[48px] rounded-2xl border-4 border-indigo-500 bg-white px-8 py-3 text-base font-extrabold text-app-primary shadow-md transition hover:bg-indigo-50 dark:bg-slate-800 dark:text-indigo-100 dark:hover:bg-slate-700 tamil-text"
            lang="ta"
          >
            மீண்டும் முயல்
          </button>
        </div>
      </div>
    </div>
  );
};
